// =============================================================================
// Automation Executor - V3-2
// Service d'exécution des automations
// =============================================================================

import { supabase } from '@/integrations/supabase/client';
import { findMatchingAutomations, type AutomationAction, type AutomationTriggerType } from './automationRepository';

export interface AutomationContext {
  from_status?: string;
  to_status?: string;
  tag_id?: string;
  assignee_id?: string;
}

// =============================================================================
// Execute matching automations for a trigger
// =============================================================================
export async function executeAutomations(
  orgId: string,
  projectId: string,
  taskId: string,
  triggerType: AutomationTriggerType,
  context: AutomationContext
): Promise<{ executed: number; errors: string[] }> {
  const errors: string[] = [];
  let executed = 0;

  try {
    const matchingAutomations = await findMatchingAutomations(orgId, projectId, triggerType, context);
    
    console.log(`[automationExecutor] Found ${matchingAutomations.length} matching automations for ${triggerType}`);

    for (const automation of matchingAutomations) {
      try {
        for (const action of automation.actions) {
          await executeAction(orgId, taskId, action);
        }
        executed++;
        console.log(`[automationExecutor] Executed automation: ${automation.name}`);
      } catch (err) {
        console.error(`[automationExecutor] Error executing automation ${automation.name}:`, err);
        errors.push(`${automation.name}: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
  } catch (err) {
    console.error('[automationExecutor] Error finding matching automations:', err);
    errors.push(`Erreur lors de la recherche des automations: ${err instanceof Error ? err.message : String(err)}`);
  }

  return { executed, errors };
}

// =============================================================================
// Execute a single action
// =============================================================================
async function executeAction(
  orgId: string,
  taskId: string,
  action: AutomationAction
): Promise<void> {
  switch (action.type) {
    case 'set_status':
      await setTaskStatus(taskId, action.value);
      break;
      
    case 'set_priority':
      await setTaskPriority(taskId, action.value);
      break;
      
    case 'set_owner':
      await setTaskOwner(taskId, action.value);
      break;
      
    case 'add_tag':
      await addTaskTag(orgId, taskId, action.value);
      break;
      
    case 'remove_tag':
      await removeTaskTag(taskId, action.value);
      break;
      
    case 'add_assignee':
      await addTaskAssignee(orgId, taskId, action.value);
      break;
      
    default:
      console.warn(`[automationExecutor] Unknown action type: ${action.type}`);
  }
}

// =============================================================================
// Individual action implementations
// =============================================================================

async function setTaskStatus(taskId: string, statusCode: string): Promise<void> {
  const { error } = await supabase
    .from('project_tasks')
    .update({ status_code: statusCode })
    .eq('id', taskId);
    
  if (error) throw error;
}

async function setTaskPriority(taskId: string, priority: string): Promise<void> {
  const { error } = await supabase
    .from('project_tasks')
    .update({ priority })
    .eq('id', taskId);
    
  if (error) throw error;
}

async function setTaskOwner(taskId: string, ownerMemberId: string): Promise<void> {
  const { error } = await supabase
    .from('project_tasks')
    .update({ owner_member_id: ownerMemberId || null })
    .eq('id', taskId);
    
  if (error) throw error;
}

async function addTaskTag(orgId: string, taskId: string, tagId: string): Promise<void> {
  // Check if tag already exists
  const { data: existing } = await supabase
    .from('project_task_tags')
    .select('task_id')
    .eq('task_id', taskId)
    .eq('tag_id', tagId)
    .maybeSingle();
    
  if (existing) return; // Already has tag
  
  const { error } = await supabase
    .from('project_task_tags')
    .insert({ task_id: taskId, org_id: orgId, tag_id: tagId });
    
  if (error) throw error;
}

async function removeTaskTag(taskId: string, tagId: string): Promise<void> {
  const { error } = await supabase
    .from('project_task_tags')
    .delete()
    .eq('task_id', taskId)
    .eq('tag_id', tagId);
    
  if (error) throw error;
}

async function addTaskAssignee(orgId: string, taskId: string, orgMemberId: string): Promise<void> {
  // Check if already assigned
  const { data: existing } = await supabase
    .from('project_task_assignees')
    .select('task_id')
    .eq('task_id', taskId)
    .eq('org_member_id', orgMemberId)
    .maybeSingle();
    
  if (existing) return; // Already assigned
  
  const { error } = await supabase
    .from('project_task_assignees')
    .insert({ task_id: taskId, org_id: orgId, org_member_id: orgMemberId });
    
  if (error) throw error;
}
